import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { ChevronLeft, Plus, Trash2, CheckCircle, AlertCircle, Package, BoxIcon, Weight, Printer, Search, Camera, Lock, Undo2, MessageSquareWarning, Truck } from "lucide-react";
import { BOX } from "@/constants/testIds";
import { FilterTabs } from "@/components/NotificationBanner";
import Toast from "@/components/Toast";
import { useAutoRefresh } from "@/hooks/useAutoRefresh";
import RefreshButton from "@/components/RefreshButton";
import BarcodeScanner from "@/components/BarcodeScanner";
import ProductImageThumb, { ProductImageButton } from "@/components/ProductImageThumb";
import ReceiveCountDialog from "@/components/ReceiveCountDialog";
import BoxingBoard from "@/components/boxing/BoxingBoard";
import DamageBand from "@/components/boxing/DamageBand";
import { getEffectiveFnsku } from "@/lib/fnskuPrint";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };
const BOX_PRESETS = { XS: [30, 20, 15], CutS: [50, 40, 30], M: [50, 40, 40], L: [60, 40, 40] };
const EDITABLE_WORK_STATES = ["PLANNING", "SUBMITTED", "BOXING"]; // PLANNING: only lines the tenant already distributed show up
const CORRECTABLE_WORK_STATES = ["PLANNING", "SUBMITTED", "BOXING"];

export default function UKWarehouse() {
  const { t } = useLang();
  const { user } = useAuth();
  const assignments = (user?.warehouse_assignments || []).filter(a => a.capabilities?.includes("RECEIVER"));
  const [chosen, setChosen] = useState("");
  const activeId = assignments.find(a => a.warehouse_id === chosen)?.warehouse_id || assignments[0]?.warehouse_id;

  if (!activeId) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400" data-testid="receiver-no-assignment">
        <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
        <p className="font-medium">{t("recv.no_assignment")}</p>
      </div>
    );
  }
  return <ReceiverWarehouse key={activeId} warehouseId={activeId} assignments={assignments} onWarehouse={setChosen} />;
}

function ReceiverWarehouse({ warehouseId, assignments, onWarehouse }) {
  const { t, tServer } = useLang();
  const [filterMode, setFilterMode] = useState("active");
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selection, setSelection] = useState(null); // { pallet, workId, tenantName }
  const [workData, setWorkData] = useState(null);
  const [selectedBoxId, setSelectedBoxId] = useState(null);
  const [boxItems, setBoxItems] = useState([]);
  const [showCreateBox, setShowCreateBox] = useState(false);
  const [boxStoreId, setBoxStoreId] = useState("");
  const [boxName, setBoxName] = useState("");
  const [showAddItem, setShowAddItem] = useState(false);
  const [addAsin, setAddAsin] = useState("");
  const [addQty, setAddQty] = useState(1);
  const [addSearchQ, setAddSearchQ] = useState("");
  const [addSearchRes, setAddSearchRes] = useState([]);
  const [showScanner, setShowScanner] = useState(false);
  const [boxDims, setBoxDims] = useState({ weight: "", length: "", width: "", height: "" });
  const [dimPreset, setDimPreset] = useState(null);
  const [showDims, setShowDims] = useState(false);
  const [confirmReceive, setConfirmReceive] = useState(null); // pallet
  const [confirmComplete, setConfirmComplete] = useState(false);
  const [confirmDeleteBox, setConfirmDeleteBox] = useState(null); // { id, name }
  const [noteDialog, setNoteDialog] = useState(null); // "return" | "correction"
  const [noteText, setNoteText] = useState("");
  const [damageDialog, setDamageDialog] = useState(null); // { items }
  const [damageItemId, setDamageItemId] = useState("");
  const [damageQty, setDamageQty] = useState("1");
  const [damageNote, setDamageNote] = useState("");
  const [msg, setMsg] = useState(null);
  const [boxItemStepperDraft, setBoxItemStepperDraft] = useState({});
  const [overAsk, setOverAsk] = useState(null); // boxing past the plan: { mode: "add" | "update", item, qty, planned, total }
  const [overReason, setOverReason] = useState("");
  const [overNote, setOverNote] = useState("");

  const clearMsg = useCallback(() => setMsg(null), []);
  const showMsg = (type, text) => setMsg({ type, text });
  const errorText = (e) => {
    const detail = e.response?.data?.detail;
    return typeof detail === "string" ? tServer(detail) : t("recv.error");
  };

  const fetchPallets = useCallback(async (silent = false) => {
    try {
      const { data } = await axios.get(`${API}/receiver-pallets`, {
        ...ax, params: { warehouse_id: warehouseId, history: filterMode === "history" },
      });
      setPallets(data);
    } catch {
      if (!silent) setMsg({ type: "error", text: t("recv.loading_error") });
    } finally {
      setLoading(false);
    }
  }, [warehouseId, filterMode, t]);

  useEffect(() => { setLoading(true); fetchPallets(); }, [fetchPallets]);

  const fetchWork = useCallback(async (workId) => {
    const { data } = await axios.get(`${API}/pallet-work/${workId}/boxing-data`, ax);
    setWorkData(data);
  }, []);

  const fetchBoxItems = async (bid) => {
    const { data } = await axios.get(`${API}/boxes/${bid}/items`, ax);
    setBoxItems(data);
  };

  // Reloads the pallet list and, when a tenant work or box is open, its data. Failures keep what is on screen.
  const reloadScreen = async () => {
    await fetchPallets(true);
    if (selection) await fetchWork(selection.workId);
    if (selectedBoxId) await fetchBoxItems(selectedBoxId);
  };
  const { refresh, refreshing } = useAutoRefresh(reloadScreen, {
    paused: showCreateBox || showAddItem || showScanner || showDims || !!confirmReceive || confirmComplete
      || !!confirmDeleteBox || !!noteDialog || !!damageDialog || !!overAsk,
  });

  const openWork = async (pallet, work) => {
    setSelection({ pallet, workId: work.work_id, tenantName: work.tenant_name });
    setSelectedBoxId(null); setBoxItems([]); setWorkData(null);
    try {
      await fetchWork(work.work_id);
    } catch (e) {
      setSelection(null);
      showMsg("error", errorText(e));
    }
  };

  const closeWork = () => {
    setSelection(null); setWorkData(null); setSelectedBoxId(null); setBoxItems([]);
    fetchPallets();
  };

  // Runs a mutation, then reloads; the server's rule message is shown as-is when it refuses.
  const act = async (fn, successKey) => {
    try {
      await fn();
      if (successKey) showMsg("success", t(successKey));
      return true;
    } catch (e) {
      showMsg("error", errorText(e));
      return false;
    }
  };

  const receivePallet = async (body) => {
    const target = confirmReceive;
    try {
      const { data } = await axios.post(`${API}/pallets/${target.id}/receive`, body, ax);
      showMsg("success", data.discrepancy_count > 0 ? t("recv.received_diff", { count: data.discrepancy_count }) : t("recv.received"));
    } catch (e) {
      showMsg("error", errorText(e));
      return false;
    }
    setConfirmReceive(null);
    await fetchPallets();
    if (selection?.pallet.id === target.id) {
      setSelection(s => ({ ...s, pallet: { ...s.pallet, physical_status: "RECEIVED" } }));
      await fetchWork(selection.workId);
    }
    return true;
  };

  const openDamageDialog = async () => {
    try {
      const { data } = await axios.get(`${API}/pallets/${selection.pallet.id}/items`, ax);
      const usable = data.filter(i => i.tenant_id === workData.work.tenant_id && (i.received_good ?? i.quantity) > 0);
      setDamageDialog({ items: usable });
      setDamageItemId(usable[0]?.id || ""); setDamageQty("1"); setDamageNote("");
    } catch (e) {
      showMsg("error", errorText(e));
    }
  };

  const submitDamage = async () => {
    const quantity = Number.parseInt(damageQty, 10) || 0;
    if (!damageItemId || quantity < 1) return;
    const ok = await act(() => axios.post(`${API}/pallet-work/${selection.workId}/damage-reports`,
      { item_id: damageItemId, quantity, ...(damageNote.trim() ? { note: damageNote.trim() } : {}) }, ax), "recv.damage_reported");
    if (!ok) return;
    setDamageDialog(null);
    await Promise.all([fetchWork(selection.workId), fetchPallets()]);
  };

  const addPhoto = async (row, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    const ok = await act(() => axios.post(`${API}/receiving-discrepancies/${row.id}/photos`, form, ax), "recv.photo_added");
    if (ok) await fetchWork(selection.workId);
  };

  const work = workData?.work;
  const pallet = selection ? (pallets.find(p => p.id === selection.pallet.id) || selection.pallet) : null;
  const received = pallet?.physical_status === "RECEIVED";
  const editable = received && EDITABLE_WORK_STATES.includes(work?.status);
  const canPrint = editable;
  const boxes = workData?.boxes || [];
  const stores = workData?.stores || [];
  const openRevisions = (workData?.revisions || []).filter(r => r.status === "OPEN");
  const discrepancies = workData?.discrepancies || [];
  const openDiscrepancies = discrepancies.filter(d => d.status === "OPEN");
  const productNames = Object.fromEntries(stores.flatMap(s => s.items.map(i => [i.asin, i.product_name])));
  const readyCount = boxes.filter(b => b.ready).length;
  const allBoxed = stores.every(s => s.items.every(i => i.remaining === 0));
  const boxableStores = stores.filter(s => s.items.some(i => i.remaining > 0));
  const canComplete = editable && work?.status !== "PLANNING" && boxes.length > 0 && allBoxed && readyCount === boxes.length
    && openRevisions.length === 0 && openDiscrepancies.length === 0;
  const canReturn = received && work?.status === "BOXING" && readyCount === 0;
  const tenantName = selection?.tenantName || "";
  const selectedBox = boxes.find(b => b.id === selectedBoxId) || null;

  const openBox = async (box) => {
    setSelectedBoxId(box.id);
    const dims = { weight: box.weight || "", length: box.length || "", width: box.width || "", height: box.height || "" };
    setBoxDims(dims);
    const matched = Object.entries(BOX_PRESETS).find(([, [l, w, h]]) =>
      Number(dims.length) === l && Number(dims.width) === w && Number(dims.height) === h);
    setDimPreset(matched ? matched[0] : (dims.length || dims.width || dims.height ? "custom" : null));
    setShowAddItem(false); setAddAsin(""); setAddQty(1); setAddSearchQ(""); setAddSearchRes([]);
    try { await fetchBoxItems(box.id); } catch (e) { showMsg("error", errorText(e)); }
  };

  const boxNameRef = useRef(null);

  // The suggested name is fetched first, so the dialog opens with it already filled in and selected.
  const openCreateBox = async (storeId = "") => {
    setBoxStoreId(storeId);
    try {
      const { data } = await axios.get(`${API}/pallets/${selection.pallet.id}/boxes/next-name`,
        { ...ax, params: { tenant_id: work.tenant_id } });
      setBoxName(data.box_name);
    } catch { setBoxName(""); }  // empty = the server names the box itself
    setShowCreateBox(true);
  };

  const createBox = async () => {
    if (!boxStoreId) return;
    try {
      const { data } = await axios.post(`${API}/pallets/${selection.pallet.id}/boxes`,
        { store_id: boxStoreId, box_name: boxName || undefined, tenant_id: work.tenant_id }, ax);
      setShowCreateBox(false); setBoxStoreId(""); setBoxName("");
      showMsg("success", t("recv.box_created"));
      await fetchWork(selection.workId);
      openBox(data);
    } catch (e) {
      showMsg("error", errorText(e));
    }
  };

  const deleteBox = async (boxId) => {
    setConfirmDeleteBox(null);
    const ok = await act(() => axios.delete(`${API}/boxes/${boxId}`, ax), "recv.box_deleted");
    if (ok) { setSelectedBoxId(null); await fetchWork(selection.workId); }
  };

  const storeRow = (asin) => stores.find(s => s.store_id === selectedBox.store_id)?.items.find(i => i.asin === asin);

  // Past the planned quantity the receiver has to give a reason first; the server refuses without one.
  const askOver = (ask) => { setOverReason(""); setOverNote(""); setOverAsk(ask); };

  const confirmOver = async () => {
    const ask = overAsk;
    const over = { over_reason: overReason, over_note: overNote.trim() };
    setOverAsk(null);
    if (ask.mode === "add") await addItemToBox(over);
    else await updateBoxItemQty(ask.item, ask.qty, over);
  };

  const addItemToBox = async (over) => {
    if (!addAsin || addQty < 1) return;
    const row = storeRow(addAsin);
    if (!over && row && row.boxed + addQty > row.planned) {
      return askOver({ mode: "add", name: row.product_name, qty: addQty, planned: row.planned, total: row.boxed + addQty });
    }
    const ok = await act(() => axios.post(`${API}/boxes/${selectedBox.id}/items`, { asin: addAsin, quantity: addQty, ...over }, ax), "recv.item_added");
    if (!ok) return;
    await Promise.all([fetchBoxItems(selectedBox.id), fetchWork(selection.workId)]);
    setAddAsin(""); setAddQty(1); setShowAddItem(false); setAddSearchQ(""); setAddSearchRes([]);
  };

  const deleteBoxItem = async (itemId) => {
    const ok = await act(() => axios.delete(`${API}/boxes/${selectedBox.id}/items/${itemId}`, ax), "recv.item_removed");
    if (ok) await Promise.all([fetchBoxItems(selectedBox.id), fetchWork(selection.workId)]);
  };

  const updateBoxItemQty = async (item, newQty, over) => {
    if (newQty < 1) return;
    const row = storeRow(item.asin);
    const total = row ? row.boxed - item.quantity + newQty : 0;
    if (!over && newQty > item.quantity && row && total > row.planned) {
      return askOver({ mode: "update", item, name: row.product_name, qty: newQty, planned: row.planned, total });
    }
    const ok = await act(() => axios.put(`${API}/boxes/${selectedBox.id}/items/${item.id}`, { quantity: newQty, ...over }, ax));
    if (ok) await Promise.all([fetchBoxItems(selectedBox.id), fetchWork(selection.workId)]);
  };

  // Same limit as the backend (MAX_BOX_SIZE): every filled field must be > 0 and <= 100.
  const dimsInvalid = ["weight", "length", "width", "height"].some(k => {
    const value = parseFloat(boxDims[k]);
    return boxDims[k] !== "" && !(value > 0 && value <= 100);
  });

  const saveDimensions = async () => {
    const payload = {};
    ["weight", "length", "width", "height"].forEach(k => { if (boxDims[k]) payload[k] = parseFloat(boxDims[k]); });
    const ok = await act(() => axios.put(`${API}/boxes/${selectedBox.id}`, payload, ax), "recv.dims_saved");
    if (ok) { await fetchWork(selection.workId); setShowDims(false); }
  };

  const setBoxReady = async (box, ready) => {
    const ok = await act(() => axios.post(`${API}/boxes/${box.id}/${ready ? "ready" : "reopen"}`, {}, ax),
      ready ? "recv.box_marked_ready" : "recv.box_reopened");
    if (ok) await Promise.all([fetchWork(selection.workId), fetchPallets()]);
  };

  const completeBoxing = async () => {
    setConfirmComplete(false);
    const ok = await act(() => axios.post(`${API}/pallet-work/${selection.workId}/complete-boxing`, {}, ax), "recv.boxing_completed");
    if (ok) await Promise.all([fetchWork(selection.workId), fetchPallets()]);
  };

  const submitNote = async () => {
    const note = noteText.trim();
    if (!note) return;
    const mode = noteDialog;
    const url = mode === "return" ? "return-to-planning" : "revisions";
    const ok = await act(() => axios.post(`${API}/pallet-work/${selection.workId}/${url}`, { note }, ax),
      mode === "return" ? "recv.returned" : "recv.correction_sent");
    if (!ok) return;
    setNoteDialog(null); setNoteText("");
    await Promise.all([fetchWork(selection.workId), fetchPallets()]);
  };

  // Store-plan items are already loaded client-side, so search/scan matching is local.
  const matchAvailableItems = (availableItems, q) => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    return availableItems.filter(i =>
      i.asin?.toLowerCase().includes(needle) ||
      i.product_name?.toLowerCase().includes(needle) ||
      getEffectiveFnsku(i).toLowerCase().includes(needle)
    ).slice(0, 10);
  };

  const onAddSearchChange = (v, availableItems) => {
    setAddSearchQ(v);
    setAddAsin("");
    setAddSearchRes(matchAvailableItems(availableItems, v));
  };

  const pickAvailableItem = (item) => {
    setAddAsin(item.asin);
    setAddSearchQ(item.product_name);
    setAddSearchRes([]);
  };

  const onScanAdd = (code, availableItems) => {
    setShowScanner(false);
    const exact = availableItems.find(i =>
      i.asin?.toLowerCase() === code.toLowerCase() || getEffectiveFnsku(i).toLowerCase() === code.toLowerCase());
    if (exact) {
      pickAvailableItem(exact);
    } else {
      setAddSearchQ(code);
      setAddSearchRes(matchAvailableItems(availableItems, code));
    }
  };

  const dialogs = (
    <ReceiveCountDialog pallet={confirmReceive} onClose={() => setConfirmReceive(null)} onSubmit={receivePallet} />
  );

  // ── Box detail view ──────────────────────────────────────
  if (selection && selectedBox) {
    const storeInfo = stores.find(s => s.store_id === selectedBox.store_id);
    const availableItems = storeInfo?.items || []; // a fully boxed line stays pickable: more can go in with a reason
    const hasDims = selectedBox.weight && selectedBox.length && selectedBox.width && selectedBox.height;
    const locked = !editable || selectedBox.ready;
    const selectedItem = availableItems.find(i => i.asin === addAsin);

    return (
      <div className="space-y-4 pb-20">
        <Toast msg={msg} onClose={clearMsg} />
        {dialogs}

        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={() => { setSelectedBoxId(null); fetchWork(selection.workId); }}
            className="gap-1.5 font-semibold border-slate-300 text-slate-700 hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4" /> {t("common.back")}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-bold text-slate-900 truncate">{selectedBox.box_name}</h1>
            <p className="text-sm text-slate-500">{selectedBox.store_name}</p>
          </div>
          <RefreshButton onClick={refresh} refreshing={refreshing} />
          {selectedBox.ready && (
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1">
              <Lock className="w-3 h-3" /> {t("recv.box_ready")}
            </span>
          )}
          <Button variant="outline" size="sm" onClick={() => setShowDims(true)} disabled={locked}
            className={`gap-1.5 h-8 text-xs ${hasDims ? "border-emerald-300 text-emerald-700" : "border-amber-300 text-amber-700"}`}>
            <Weight className="w-3.5 h-3.5" />
            {hasDims ? `${selectedBox.weight}kg` : t("recv.set_dims")}
          </Button>
          {editable && (selectedBox.ready ? (
            <Button size="sm" variant="outline" className="gap-1.5" onClick={() => setBoxReady(selectedBox, false)} data-testid="reopen-box-btn">
              <Undo2 className="w-4 h-4" /> {t("recv.reopen_box")}
            </Button>
          ) : (
            <Button size="sm" className="gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => setBoxReady(selectedBox, true)}
              disabled={!hasDims || boxItems.length === 0} data-testid="mark-box-ready-btn">
              <CheckCircle className="w-4 h-4" /> {t("recv.mark_ready")}
            </Button>
          ))}
        </div>

        {selectedBox.ready && editable && (
          <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-700 text-sm">
            <Lock className="w-4 h-4 shrink-0" /> {t("recv.box_locked")}
          </div>
        )}
        {!hasDims && !locked && (
          <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm">
            <AlertCircle className="w-4 h-4 shrink-0" /> {t("recv.dims_required")}
          </div>
        )}

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
            <span className="font-semibold text-slate-900">{t("recv.box_items", { count: boxItems.length })}</span>
            {!locked && availableItems.length > 0 && (
              <Button size="sm" onClick={() => setShowAddItem(!showAddItem)} className="h-8 text-xs gap-1 bg-blue-600 hover:bg-blue-700">
                <Plus className="w-3 h-3" /> {t("recv.add_item")}
              </Button>
            )}
          </div>

          {showAddItem && !locked && (
            <div className="p-4 bg-blue-50 border-b border-blue-100 space-y-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input value={addSearchQ} onChange={e => onAddSearchChange(e.target.value, availableItems)}
                    placeholder={t("recv.search_placeholder")} className="pl-9 h-11" />
                  {addSearchRes.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-xl shadow-lg max-h-96 overflow-y-auto">
                      {addSearchRes.map(i => (
                        <button key={i.asin} onClick={() => pickAvailableItem(i)}
                          className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b last:border-0 border-slate-50">
                          <p className="text-sm font-medium text-slate-900">{i.product_name}</p>
                          <p className="text-[10px] text-slate-500 font-mono">FNSKU: {getEffectiveFnsku(i) || "—"} · {t("recv.max", { count: i.remaining })}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowScanner(true)} className="h-11 w-11 p-0 shrink-0">
                  <Camera className="w-5 h-5" />
                </Button>
              </div>

              {selectedItem && (
                <div className="space-y-2">
                  <div className="p-3 bg-white rounded-xl border border-blue-200 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-slate-900 text-sm">{selectedItem.product_name}</p>
                      <p className="text-xs text-slate-500 font-mono">FNSKU: {getEffectiveFnsku(selectedItem) || "—"}</p>
                    </div>
                    <ProductImageThumb asin={addAsin} alt={selectedItem.product_name} fnsku={getEffectiveFnsku(selectedItem)} />
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" className="h-11 w-11 p-0 text-lg" onClick={() => setAddQty(Math.max(1, addQty - 1))}>−</Button>
                    <Input type="number" inputMode="numeric" value={addQty} min={1}
                      onChange={e => setAddQty(parseInt(e.target.value) || 1)} onFocus={e => e.target.select()}
                      className="text-center h-11 text-base flex-1" />
                    <Button variant="outline" className="h-11 w-11 p-0 text-lg" onClick={() => setAddQty(addQty + 1)}>+</Button>
                    <Button className="h-11 bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={() => addItemToBox()}>{t("common.add")}</Button>
                  </div>
                  <button type="button" className="text-xs text-blue-600 hover:underline" onClick={() => setAddQty(selectedItem.remaining || 1)}>
                    {t("recv.set_max", { count: selectedItem.remaining })}
                  </button>
                </div>
              )}
            </div>
          )}

          {showScanner && <BarcodeScanner onScan={(code) => onScanAdd(code, availableItems)} onClose={() => setShowScanner(false)} />}

          {boxItems.length === 0 ? (
            <div className="p-8 text-center text-slate-400">
              <BoxIcon className="w-8 h-8 mx-auto mb-2 opacity-40" />
              <p className="text-sm">{t("recv.box_empty")}</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-50">
              {boxItems.map(item => (
                <div key={item.id} className="flex items-center gap-3 px-4 py-3">
                  <ProductImageButton asin={item.asin} name={item.product_name}
                    fnsku={getEffectiveFnsku(storeInfo?.items.find(i => i.asin === item.asin))} />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-900 text-sm truncate">{item.product_name}</p>
                    <p className="text-xs text-slate-500 font-mono">{item.asin}</p>
                    {item.over_reason && storeInfo?.items.find(i => i.asin === item.asin)?.over > 0 && (
                      <p className="text-xs font-medium text-amber-800">
                        <span className="mr-1.5 rounded bg-amber-200 px-1.5 py-px font-bold">
                          {t("recv.over_badge", { count: storeInfo.items.find(i => i.asin === item.asin).over })}
                        </span>
                        {t(`recv.over_r.${item.over_reason}`)}{item.over_note ? ` · ${item.over_note}` : ""}
                      </p>
                    )}
                  </div>
                  {locked ? (
                    <span className="font-bold text-sm text-slate-900 shrink-0">{item.quantity}</span>
                  ) : (
                    <div className="flex items-center shrink-0">
                      <button className="flex items-center justify-center w-[44px] h-[44px] disabled:opacity-35"
                        onClick={() => updateBoxItemQty(item, item.quantity - 1)} disabled={item.quantity <= 1}>
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-bold text-lg leading-none select-none transition-colors">−</span>
                      </button>
                      <input inputMode="numeric"
                        className="w-11 h-9 text-center font-bold text-sm border border-slate-200 rounded-lg bg-white text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500 cursor-text"
                        value={boxItemStepperDraft[item.id] ?? String(item.quantity)}
                        onFocus={e => { setBoxItemStepperDraft(p => ({ ...p, [item.id]: String(item.quantity) })); e.target.select(); }}
                        onChange={e => setBoxItemStepperDraft(p => ({ ...p, [item.id]: e.target.value }))}
                        onBlur={() => {
                          const v = parseInt(boxItemStepperDraft[item.id], 10);
                          if (!isNaN(v) && v >= 1 && v !== item.quantity) updateBoxItemQty(item, v);
                          setBoxItemStepperDraft(p => { const n = { ...p }; delete n[item.id]; return n; });
                        }}
                        onKeyDown={e => { if (e.key === "Enter") e.target.blur(); }} />
                      <button className="flex items-center justify-center w-[44px] h-[44px]" onClick={() => updateBoxItemQty(item, item.quantity + 1)}>
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 text-slate-700 font-bold text-lg leading-none select-none transition-colors">+</span>
                      </button>
                      <button className="flex items-center justify-center w-[44px] h-[44px] text-red-400 hover:text-red-600" onClick={() => deleteBoxItem(item.id)}>
                        <span className="flex items-center justify-center w-9 h-9 rounded-lg hover:bg-red-50 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </span>
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <Dialog open={!!overAsk} onOpenChange={open => { if (!open) setOverAsk(null); }}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t("recv.over_title")}</DialogTitle>
              {overAsk && (
                <p className="text-sm text-muted-foreground">
                  {t("recv.over_body", { name: overAsk.name, planned: overAsk.planned, boxed: overAsk.total, count: overAsk.total - overAsk.planned })}
                </p>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label>{t("recv.over_reason")} *</Label>
                <Select value={overReason} onValueChange={setOverReason}>
                  <SelectTrigger className="h-11" data-testid="over-reason"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["EXTRA_ON_PALLET", "COUNT_DIFF", "OTHER"].map(code => (
                      <SelectItem key={code} value={code}>{t(`recv.over_r.${code}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{t("recv.over_note")}{overReason === "OTHER" ? " *" : ""}</Label>
                <Input value={overNote} onChange={e => setOverNote(e.target.value)} maxLength={200} className="h-11" data-testid="over-note" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOverAsk(null)}>{t("common.cancel")}</Button>
              <Button onClick={confirmOver} disabled={!overReason || (overReason === "OTHER" && !overNote.trim())}
                className="bg-amber-600 hover:bg-amber-700" data-testid="over-confirm">{t("recv.over_confirm")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showDims} onOpenChange={setShowDims}>
          <DialogContent className="max-w-sm">
            <DialogHeader><DialogTitle>{t("recv.dims_title")}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>{t("uk.weight")} *</Label>
                <Input type="number" inputMode="decimal" step="0.01" value={boxDims.weight}
                  onChange={e => setBoxDims(d => ({ ...d, weight: e.target.value }))} onFocus={e => e.target.select()}
                  placeholder="0.00" className="h-11" autoFocus data-testid={BOX.weightInput} />
              </div>
              <div className="space-y-2">
                <Label>{t("recv.box_size")}</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(BOX_PRESETS).map(([key, [l, w, h]]) => (
                    <button key={key} type="button"
                      onClick={() => { setDimPreset(key); setBoxDims(d => ({ ...d, length: String(l), width: String(w), height: String(h) })); }}
                      className={`flex flex-col items-start px-3 py-2.5 rounded-xl border text-left transition-all ${dimPreset === key ? "border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-400" : "border-slate-200 hover:border-blue-300 hover:bg-slate-50 text-slate-700"}`}>
                      <span className="font-semibold text-sm">Box {key}</span>
                      <span className="text-xs text-slate-400 mt-0.5">{l}×{w}×{h} cm</span>
                    </button>
                  ))}
                </div>
                <button type="button"
                  onClick={() => { setDimPreset("custom"); setBoxDims(d => ({ ...d, length: "", width: "", height: "" })); }}
                  className={`w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${dimPreset === "custom" ? "border-blue-500 bg-blue-50 text-blue-700 ring-1 ring-blue-400" : "border-dashed border-slate-300 hover:border-blue-300 hover:bg-slate-50 text-slate-500"}`}>
                  + {t("recv.custom_size")}
                </button>
              </div>
              {dimPreset === "custom" && (
                <div className="space-y-1.5">
                  <Label>{t("uk.length")} × {t("uk.width")} × {t("uk.height")} (cm)</Label>
                  <div className="flex items-center gap-2">
                    <Input type="number" inputMode="decimal" step="0.1" value={boxDims.length}
                      onChange={e => setBoxDims(d => ({ ...d, length: e.target.value }))} onFocus={e => e.target.select()}
                      placeholder="L" className="h-10 text-center" data-testid={BOX.lengthInput} />
                    <span className="text-slate-400 font-bold shrink-0">×</span>
                    <Input type="number" inputMode="decimal" step="0.1" value={boxDims.width}
                      onChange={e => setBoxDims(d => ({ ...d, width: e.target.value }))} onFocus={e => e.target.select()}
                      placeholder="W" className="h-10 text-center" data-testid={BOX.widthInput} />
                    <span className="text-slate-400 font-bold shrink-0">×</span>
                    <Input type="number" inputMode="decimal" step="0.1" value={boxDims.height}
                      onChange={e => setBoxDims(d => ({ ...d, height: e.target.value }))} onFocus={e => e.target.select()}
                      placeholder="H" className="h-10 text-center" data-testid={BOX.heightInput} />
                  </div>
                </div>
              )}
              {dimsInvalid && <p className="text-sm text-red-600">{t("recv.dims_range")}</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDims(false)}>{t("common.cancel")}</Button>
              <Button onClick={saveDimensions} disabled={dimsInvalid} data-testid={BOX.saveBtn} className="bg-blue-600 hover:bg-blue-700">{t("common.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ── Tenant work detail / store plan ──────────────────────
  if (selection) {
    return (
      <div className="space-y-4 pb-20">
        <Toast msg={msg} onClose={clearMsg} />
        {dialogs}

        <div className="flex items-center gap-3 flex-wrap">
          <Button variant="outline" size="sm" onClick={closeWork} className="gap-1.5 font-semibold border-slate-300 text-slate-700 hover:bg-slate-100">
            <ChevronLeft className="w-4 h-4" /> {t("common.back")}
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{pallet.pallet_name}</h1>
            <p className="text-sm text-slate-500 truncate">{tenantName}</p>
          </div>
          <StatusBadge status={pallet.physical_status} />
          {work && <StatusBadge status={work.status} />}
          <RefreshButton onClick={refresh} refreshing={refreshing} />
        </div>

        {!workData && <p className="text-sm text-slate-400">{t("common.loading")}</p>}

        {workData && (
          <>
            <div className="flex flex-wrap gap-2 justify-end">
              {CORRECTABLE_WORK_STATES.includes(work.status) && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setNoteText(""); setNoteDialog("correction"); }} data-testid="request-correction-btn">
                  <MessageSquareWarning className="w-4 h-4" /> {t("recv.request_correction")}
                </Button>
              )}
              {canReturn && (
                <Button size="sm" variant="outline" className="gap-1.5" onClick={() => { setNoteText(""); setNoteDialog("return"); }} data-testid="return-to-planning-btn">
                  <Undo2 className="w-4 h-4" /> {t("recv.return_planning")}
                </Button>
              )}
              {editable && (
                <>
                  {work.status === "BOXING" && (
                    <Button size="sm" variant="outline" className="gap-1.5" onClick={openDamageDialog} data-testid="report-damage-btn">
                      <AlertCircle className="w-4 h-4" /> {t("recv.damage_report")}
                    </Button>
                  )}
                  <Button size="sm" onClick={() => openCreateBox()} className="gap-1.5 bg-blue-600 hover:bg-blue-700" data-testid={BOX.createBtn}>
                    <Plus className="w-4 h-4" /> {t("uk.create_box")}
                  </Button>
                  <Button size="sm" disabled={!canComplete} onClick={() => setConfirmComplete(true)}
                    className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-slate-300" data-testid="complete-boxing-btn">
                    <CheckCircle className="w-4 h-4" /> {t("recv.complete_boxing")}
                  </Button>
                </>
              )}
            </div>

            {!received && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-700 text-sm flex-wrap">
                <Truck className="w-4 h-4 shrink-0" />
                <span className="flex-1">{t("recv.receive_first")}</span>
                {pallet.physical_status === "IN_TRANSIT" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirmReceive(pallet)}>{t("recv.receive_pallet")}</Button>
                )}
              </div>
            )}
            {received && work.status === "PLANNING" && (
              <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 text-sm">
                <AlertCircle className="w-4 h-4 shrink-0" /> {t("recv.planning_pending")}
              </div>
            )}
            {(work.status === "BOXED" || work.status === "COMPLETED") && (
              <div className="flex items-center gap-2 p-3 bg-slate-100 border border-slate-200 rounded-xl text-slate-600 text-sm">
                <CheckCircle className="w-4 h-4 shrink-0" /> {t("recv.read_only")}
              </div>
            )}
            {openRevisions.map(revision => (
              <div key={revision.id} className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-red-600 shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <p className="font-semibold text-red-800 text-sm">{t("recv.revision_open")}</p>
                  <p className="text-xs text-red-600">{new Date(revision.requested_at).toLocaleString()} · {revision.requested_by_name}</p>
                  <p className="text-sm text-red-700 mt-1">{revision.note}</p>
                </div>
              </div>
            ))}
            {discrepancies.length > 0 && (
              <DamageBand rows={discrepancies} names={productNames} actions={row => (
                <div className="flex items-center gap-3 flex-wrap">
                  {row.status === "OPEN" && <span className="text-xs text-amber-700">{t("recv.discrepancy_waiting")}</span>}
                  {work.status !== "BOXED" && (row.photo_paths?.length || 0) < 5 && (
                    <label className="text-xs font-semibold text-blue-600 cursor-pointer flex items-center gap-1">
                      <Camera className="w-3.5 h-3.5" /> {t("recv.photo_add")}
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={e => { addPhoto(row, e.target.files?.[0]); e.target.value = ""; }} />
                    </label>
                  )}
                </div>
              )} />
            )}

            <BoxingBoard workStatus={work.status} stores={stores} boxes={boxes}
              discrepancies={discrepancies} editable={editable} canPrint={canPrint} onAddBox={openCreateBox} onOpenBox={openBox}
              onDeleteBox={box => setConfirmDeleteBox({ id: box.id, name: box.box_name })}
              onMessage={showMsg} onChanged={() => Promise.all([fetchWork(selection.workId), fetchPallets()])} />

            <Dialog open={showCreateBox} onOpenChange={setShowCreateBox}>
              <DialogContent className="max-w-sm"
                onOpenAutoFocus={e => { e.preventDefault(); boxNameRef.current?.focus(); boxNameRef.current?.select(); }}>
                <DialogHeader><DialogTitle>{t("uk.create_box")}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>{t("common.store")} *</Label>
                    <Select value={boxStoreId} onValueChange={setBoxStoreId}>
                      <SelectTrigger className="h-11"><SelectValue placeholder={t("recv.select_store")} /></SelectTrigger>
                      <SelectContent>
                        {boxableStores.map(s => <SelectItem key={s.store_id} value={s.store_id}>{s.store_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {boxableStores.length === 0 && <p className="text-xs text-amber-600">{t("recv.no_store_left")}</p>}
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("uk.box_name")} ({t("adm.optional")})</Label>
                    <Input ref={boxNameRef} value={boxName} onChange={e => setBoxName(e.target.value)} placeholder={t("recv.box_name_placeholder")}
                      className="h-11" onKeyDown={e => e.key === "Enter" && createBox()} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setShowCreateBox(false)}>{t("common.cancel")}</Button>
                  <Button onClick={createBox} disabled={!boxStoreId} className="bg-blue-600 hover:bg-blue-700">{t("common.create")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={!!noteDialog} onOpenChange={open => { if (!open) setNoteDialog(null); }}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>{t(noteDialog === "return" ? "recv.return_title" : "recv.correction_title")}</DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">{t(noteDialog === "return" ? "recv.return_body" : "recv.correction_body")}</p>
                <div className="space-y-1.5">
                  <Label>{t("common.notes")} *</Label>
                  <Textarea value={noteText} onChange={e => setNoteText(e.target.value)} maxLength={1000} rows={4}
                    placeholder={t("recv.note_placeholder")} data-testid="recv-note-input" />
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNoteDialog(null)}>{t("common.cancel")}</Button>
                  <Button onClick={submitNote} disabled={!noteText.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="recv-note-submit">
                    {t("recv.send")}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Dialog open={!!damageDialog} onOpenChange={open => { if (!open) setDamageDialog(null); }}>
              <DialogContent className="max-w-sm">
                <DialogHeader>
                  <DialogTitle>{t("recv.damage_title")}</DialogTitle>
                  <p className="text-sm text-muted-foreground">{t("recv.damage_body")}</p>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <Label>{t("recv.damage_item")} *</Label>
                    <Select value={damageItemId} onValueChange={setDamageItemId}>
                      <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {(damageDialog?.items || []).map(i => (
                          <SelectItem key={i.id} value={i.id}>
                            {i.product_name} ({t("recv.damage_good_left", { count: i.received_good ?? i.quantity })})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("recv.damage_qty")} *</Label>
                    <Input inputMode="numeric" value={damageQty} onChange={e => setDamageQty(e.target.value.replace(/\D/g, ""))} className="h-11" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>{t("common.notes")}</Label>
                    <Textarea value={damageNote} onChange={e => setDamageNote(e.target.value)} maxLength={500} rows={3} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setDamageDialog(null)}>{t("common.cancel")}</Button>
                  <Button onClick={submitDamage} disabled={!damageItemId || !(Number.parseInt(damageQty, 10) > 0)}
                    className="bg-blue-600 hover:bg-blue-700" data-testid="damage-submit">{t("recv.send")}</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <AlertDialog open={!!confirmDeleteBox} onOpenChange={() => setConfirmDeleteBox(null)}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("adm.delete_title", { name: confirmDeleteBox?.name })}</AlertDialogTitle>
                  <p className="text-sm text-muted-foreground">{t("recv.delete_box_body")}</p>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={() => deleteBox(confirmDeleteBox.id)} className="bg-red-600 hover:bg-red-700">{t("recv.delete_box")}</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={confirmComplete} onOpenChange={setConfirmComplete}>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("recv.complete_title", { tenant: tenantName })}</AlertDialogTitle>
                  <p className="text-sm text-muted-foreground">{t("recv.complete_body")}</p>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction onClick={completeBoxing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-complete-boxing">
                    {t("recv.complete_boxing")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </>
        )}
      </div>
    );
  }

  // ── Pallet list ──────────────────────────────────────────
  return (
    <div className="space-y-4 pb-4">
      <Toast msg={msg} onClose={clearMsg} />
      {dialogs}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-extrabold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>
          {t("nav.receiver_warehouse")}
        </h1>
        <div className="flex items-center gap-3 flex-wrap">
          {assignments.length > 1 && (
            <Select value={warehouseId} onValueChange={onWarehouse}>
              <SelectTrigger className="h-9 w-56" aria-label={t("recv.warehouse")} data-testid="receiver-warehouse-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {assignments.map(a => <SelectItem key={a.warehouse_id} value={a.warehouse_id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <FilterTabs mode={filterMode} onChange={setFilterMode} labels={{ active: t("planning.active"), history: t("planning.history") }} />
          <RefreshButton onClick={refresh} refreshing={refreshing} />
        </div>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">{t("common.loading")}</p>
      ) : pallets.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-400">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p className="font-medium">{t(filterMode === "history" ? "recv.empty_history" : "recv.empty_active")}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {pallets.map(p => (
            <div key={p.id} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 space-y-3" data-testid={`receiver-pallet-${p.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-slate-900 truncate">{p.pallet_name}</h3>
                  <p className="text-xs text-slate-500">{p.shipped_at ? new Date(p.shipped_at).toLocaleDateString() : ""}</p>
                </div>
                <StatusBadge status={p.physical_status} />
              </div>
              {p.physical_status === "IN_TRANSIT" && (
                <Button size="sm" className="w-full gap-1.5 bg-emerald-600 hover:bg-emerald-700" onClick={() => setConfirmReceive(p)} data-testid={`receive-pallet-${p.id}`}>
                  <Truck className="w-4 h-4" /> {t("recv.receive_pallet")}
                </Button>
              )}
              <div className="space-y-2">
                {p.works.map(w => (
                  <button key={w.work_id} type="button" onClick={() => openWork(p, w)}
                    className="w-full text-left rounded-xl border border-slate-200 hover:border-blue-300 hover:bg-slate-50 transition-colors px-3 py-2"
                    data-testid={`receiver-work-${w.work_id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm text-slate-900 truncate">{w.tenant_name}</span>
                      <StatusBadge status={w.status} />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      {t("recv.items_units", { count: w.item_count, units: w.total_quantity })}
                      {w.box_count > 0 && <> · {t("recv.boxes_ready", { ready: w.ready_box_count, count: w.box_count })}</>}
                    </p>
                    {w.labels_waiting_box_count > 0 && (
                      <p className="text-xs text-blue-700 font-semibold mt-1 flex items-center gap-1" data-testid={`labels-waiting-${w.work_id}`}>
                        <Printer className="w-3 h-3" /> {t("labels.waiting", { count: w.labels_waiting_box_count })}
                      </p>
                    )}
                    {w.has_open_revision && (
                      <p className="text-xs text-red-600 font-semibold mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {t("recv.revision_open")}
                      </p>
                    )}
                    {w.has_open_discrepancy && (
                      <p className="text-xs text-amber-700 font-semibold mt-1 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {t("recv.discrepancy_waiting")}
                      </p>
                    )}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
